import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPrices } from '../../services/storage';
import { PRICE_DATA, SECTION_IMAGES } from './PreisePage';
import type { PriceSection } from './PreisePage';

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const PriceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useLanguage();
  const [pricesMap, setPricesMap] = useState<Record<string, string>>({});

  const baseSection: PriceSection | undefined =
    (location.state as { section?: PriceSection })?.section ??
    PRICE_DATA.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
    getPrices().then(setPricesMap).catch(() => {});
  }, [id]);

  if (!baseSection) {
    return (
      <Container maxWidth="lg" sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Preisbereich nicht gefunden</Typography>
        <Button component={RouterLink} to="/preise" startIcon={<ArrowBackIcon />}>Zurück zu Preisen</Button>
      </Container>
    );
  }

  const section: PriceSection = {
    ...baseSection,
    items: baseSection.items.map((item) => ({
      ...item,
      price: pricesMap[item.labelKey] ?? item.price,
    })),
  };

  const imgSrc = SECTION_IMAGES[section.id] ?? SECTION_IMAGES.einrichtung;

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 60%)',
          borderBottom: '1px solid #e8e8e8',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/preise"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'text.secondary', mb: 5, px: 0, fontWeight: 400, '&:hover': { bgcolor: 'transparent', color: '#111', textDecoration: 'none' } }}
          >
            {t('nav.preise')}
          </Button>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6, alignItems: { md: 'center' } }}>
            <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
                {t('preise.lohn_chip')}
              </Typography>
              <Typography
                variant="h1"
                sx={{ fontSize: { xs: '2.2rem', md: '3.5rem' }, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.1, mb: 4, color: '#3d3d3d' }}
              >
                {t(section.titleKey)}
              </Typography>
              {section.subtitleKey && (
                <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555', mb: 4 }}>
                  {t(section.subtitleKey)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  startIcon={<CalendarMonthIcon />}
                  sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                >
                  Beratung anfragen
                </Button>
                <Button
                  component={RouterLink}
                  to="/preise"
                  variant="outlined"
                  size="large"
                  sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                >
                  Alle Preise
                </Button>
              </Box>
            </MotionBox>

            <MotionBox
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              sx={{ flex: 1 }}
            >
              <Box
                component="img"
                src={imgSrc}
                alt={t(section.titleKey)}
                sx={{ width: '100%', height: { xs: 240, md: 380 }, objectFit: 'cover', borderRadius: 3, boxShadow: '0 24px 60px rgba(0,0,0,0.14)' }}
              />
            </MotionBox>
          </Box>
        </Container>
      </Box>

      {/* ── Price List ── */}
      <Box sx={{ bgcolor: '#f8f8f8', py: { xs: 8, md: 12 }, borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg">
          <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} sx={{ mb: 6 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
              Preisübersicht
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800 }}>
              Alle Leistungen &amp; Preise
            </Typography>
          </MotionBox>

          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 3,
              border: '1px solid #e8e8e8',
              overflow: 'hidden',
              boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
            }}
          >
            {section.items.map((item, i) => (
              <MotionBox
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 3,
                    px: { xs: 3, md: 5 },
                    py: 2.5,
                    bgcolor: item.highlight ? '#f0f4ff' : 'transparent',
                    borderLeft: item.highlight ? '3px solid #1737bc' : '3px solid transparent',
                    '&:not(:last-child)': { borderBottom: '1px solid #f0f0f0' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: item.highlight ? '#1737bc' : '#aaa', flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontWeight: item.highlight ? 600 : 400, fontSize: '0.95rem', color: '#222' }}>
                        {t(item.labelKey)}
                      </Typography>
                      {item.noteKey && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mt: 0.25 }}>
                          {t(item.noteKey)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: item.highlight ? '#1737bc' : '#0d1333',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.price}
                  </Typography>
                </Box>
                {i < section.items.length - 1 && <Divider sx={{ mx: 3, opacity: 0.4 }} />}
              </MotionBox>
            ))}
          </Box>

          {/* Legal note */}
          <Box
            sx={{
              mt: 4,
              bgcolor: '#fff',
              border: '1px solid #e8e8e8',
              borderLeft: '4px solid #1e3a5f',
              borderRadius: 2,
              px: 4,
              py: 2.5,
            }}
          >
            <Typography sx={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.8 }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#1e3a5f' }}>
                {t('preise.lohn_legal').split(':')[0]}:{' '}
              </Box>
              {t('preise.lohn_legal').split(':').slice(1).join(':')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: '#0d1333' }}>
              {t('preise.lohn_cta_title')}
            </Typography>
            <Typography sx={{ color: '#666', mb: 5, maxWidth: 440, mx: 'auto', lineHeight: 1.8 }}>
              {t('preise.lohn_cta_text')}
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              sx={{ px: 5, py: 1.75, fontSize: '1rem', fontWeight: 700 }}
            >
              {t('preise.lohn_cta_btn')}
            </Button>
          </MotionBox>
        </Container>
      </Box>

    </Box>
  );
};

export default PriceDetailPage;
